<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, prettier/prettier -->
<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, prettier/prettier */
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';

const props = defineProps({
  module: { type: String, required: true },
  screen: { type: String, default: '' },
});

const route = useRoute();
const router = useRouter();
const currentUser = useMapGetter('getCurrentUser');

const navigationItem = (label, routeName = '', options = {}) => ({
  activeOn: [],
  query: {},
  ...options,
  label,
  routeName,
});

const initials = value =>
  String(value || '—')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(item => item[0])
    .join('')
    .toUpperCase();

const currentUserName = computed(
  () => currentUser.value?.name || currentUser.value?.available_name || 'Usuário'
);
const currentUserRole = computed(
  () => currentUser.value?.role || currentUser.value?.type || 'Perfil'
);
const currentUserInitials = computed(() => initials(currentUserName.value));

const hideNavigation = computed(
  () => props.module === 'proposals' && ['edit', 'create'].includes(props.screen)
);

const configuration = computed(() => {
  const byModule = {
    sales: {
      active: 'Vendas',
      wide: true,
      width: 'w-[178px]',
      items: [
        navigationItem('Início', 'inicio_index'),
        navigationItem('Clientes', 'contacts_dashboard_index'),
        navigationItem('Vendas', 'operacao_imobiliaria_vendas', {
          activeOn: [
            'operacao_imobiliaria_vendas_detalhes',
            'operacao_imobiliaria_vendas_revisar',
            'operacao_imobiliaria_vendas_cadastro',
            'operacao_imobiliaria_vendas_nova',
          ],
        }),
        navigationItem('Propostas', 'lais_tenants_rent_flows', {
          activeOn: [
            'lais_tenants_rent_flows_status',
            'lais_tenants_rent_flows_status_phase',
          ],
        }),
        navigationItem('Visitas', 'visitas_index', {
          activeOn: ['visita_show', 'visita_edit', 'visita_new'],
        }),
        navigationItem('Pós-venda', 'operacao_imobiliaria_pos_venda', {
          activeOn: [
            'operacao_imobiliaria_pos_venda_detalhes',
            'operacao_imobiliaria_pos_venda_status',
          ],
        }),
        navigationItem('Financeiro', 'operacao_imobiliaria_financeiro', {
          activeOn: [
            'financeiro_index',
            'operacao_imobiliaria_financeiro_detalhes',
            'operacao_imobiliaria_financeiro_status',
            'operacao_imobiliaria_financeiro_status_alterar',
          ],
        }),
        navigationItem('Corretores', 'operacao_imobiliaria_corretor'),
      ],
    },
    proposals: {
      active: 'Propostas',
      compact: true,
      width: 'w-[54px]',
      items: [
        navigationItem('Painel', 'inicio_index'),
        navigationItem('Imóveis', 'imoveis_index'),
        navigationItem('Leads', 'lais_leads_index'),
        navigationItem('Clientes', 'contacts_dashboard_index'),
        navigationItem('Propostas', 'lais_tenants_rent_flows', {
          activeOn: [
            'lais_tenants_rent_flows_status',
            'lais_tenants_rent_flows_status_phase',
          ],
        }),
        navigationItem('Visitas', 'visitas_index', {
          activeOn: ['visita_show', 'visita_edit', 'visita_new'],
        }),
        navigationItem('Negócios'),
      ],
    },
    visits: {
      active: 'Visitas',
      wide: true,
      width: 'w-[211px]',
      items: [
        navigationItem('Painel', 'inicio_index'),
        navigationItem('Imóveis', 'imoveis_index'),
        navigationItem('Leads', 'lais_leads_index'),
        navigationItem('Clientes', 'contacts_dashboard_index'),
        navigationItem('Visitas', 'visitas_index', {
          activeOn: ['visita_show', 'visita_edit', 'visita_new'],
        }),
        navigationItem('Propostas', 'lais_tenants_rent_flows', {
          activeOn: [
            'lais_tenants_rent_flows_status',
            'lais_tenants_rent_flows_status_phase',
          ],
        }),
        navigationItem('Negócios', 'operacao_imobiliaria_vendas', {
          activeOn: [
            'operacao_imobiliaria_vendas_detalhes',
            'operacao_imobiliaria_vendas_revisar',
            'operacao_imobiliaria_vendas_cadastro',
            'operacao_imobiliaria_vendas_nova',
          ],
        }),
        navigationItem('Mensagens', 'home'),
        navigationItem('Tarefas'),
        navigationItem('Marketing'),
        navigationItem('Relatórios', 'relatorio_index'),
        navigationItem('Configurações', 'general_settings_index'),
      ],
    },
    postSale: {
      active: 'Pós-venda',
      wide: true,
      width: 'w-[213px]',
      items: [
        navigationItem('Visão geral', 'inicio_index'),
        navigationItem('Dashboard'),
        navigationItem('Oportunidades'),
        navigationItem('Imóveis', 'imoveis_index'),
        navigationItem('Clientes', 'contacts_dashboard_index'),
        navigationItem('Leads', 'lais_leads_index'),
        navigationItem('Atividades'),
        navigationItem('Pós-venda', 'operacao_imobiliaria_pos_venda', {
          activeOn: [
            'operacao_imobiliaria_pos_venda_detalhes',
            'operacao_imobiliaria_pos_venda_status',
          ],
        }),
        navigationItem('Tarefas'),
        navigationItem('Processos'),
        navigationItem('Documentos'),
        navigationItem('Recebimentos'),
        navigationItem('Repasse'),
        navigationItem('Equipe'),
        navigationItem('Modelos'),
        navigationItem('Configurações', 'general_settings_index'),
      ],
    },
    finance: {
      active: 'Financeiro',
      wide: true,
      width: 'w-[213px]',
      items: [
        navigationItem('Dashboard', 'inicio_index'),
        navigationItem('Imóveis', 'imoveis_index'),
        navigationItem('Leads', 'lais_leads_index'),
        navigationItem('Clientes', 'contacts_dashboard_index'),
        navigationItem('Atendimentos', 'home'),
        navigationItem('Propostas', 'lais_tenants_rent_flows', {
          activeOn: [
            'lais_tenants_rent_flows_status',
            'lais_tenants_rent_flows_status_phase',
          ],
        }),
        navigationItem('Vendas', 'operacao_imobiliaria_vendas', {
          activeOn: [
            'operacao_imobiliaria_vendas_detalhes',
            'operacao_imobiliaria_vendas_revisar',
            'operacao_imobiliaria_vendas_cadastro',
            'operacao_imobiliaria_vendas_nova',
          ],
        }),
        navigationItem('Financeiro', 'operacao_imobiliaria_financeiro', {
          activeOn: [
            'financeiro_index',
            'operacao_imobiliaria_financeiro_detalhes',
            'operacao_imobiliaria_financeiro_status',
            'operacao_imobiliaria_financeiro_status_alterar',
          ],
        }),
        navigationItem('Comissões', 'financeiro_comissoes_index', {
          activeOn: ['operacao_imobiliaria_vendas_comissao'],
        }),
        navigationItem('Relatórios', 'relatorio_index'),
        navigationItem('Configurações', 'general_settings_index'),
      ],
    },
    brokers: {
      active: 'Corretores',
      compact: props.screen !== 'create',
      wide: props.screen === 'create',
      width: props.screen === 'create' ? 'w-[198px]' : 'w-[98px]',
      items: props.screen === 'create'
        ? [
            navigationItem('Início', 'inicio_index'),
            navigationItem('Descobrir'),
            navigationItem('Feed'),
            navigationItem('Mensagens', 'home'),
            navigationItem('Minha equipe'),
            navigationItem('Corretores', 'operacao_imobiliaria_corretor'),
            navigationItem('Imóveis', 'imoveis_index'),
            navigationItem('Relatórios', 'relatorio_index'),
            navigationItem('Configurações', 'general_settings_index'),
          ]
        : [
            navigationItem('Dashboard', 'inicio_index'),
            navigationItem('Imóveis', 'imoveis_index'),
            navigationItem('Leads', 'lais_leads_index'),
            navigationItem('Clientes', 'contacts_dashboard_index'),
            navigationItem('Atendimentos', 'home'),
            navigationItem('Propostas', 'lais_tenants_rent_flows', {
              activeOn: [
                'lais_tenants_rent_flows_status',
                'lais_tenants_rent_flows_status_phase',
              ],
            }),
            navigationItem('Vendas', 'operacao_imobiliaria_vendas', {
              activeOn: [
                'operacao_imobiliaria_vendas_detalhes',
                'operacao_imobiliaria_vendas_revisar',
                'operacao_imobiliaria_vendas_cadastro',
                'operacao_imobiliaria_vendas_nova',
              ],
            }),
            navigationItem('Corretores', 'operacao_imobiliaria_corretor'),
            navigationItem('Relatórios', 'relatorio_index'),
            navigationItem('Configurações', 'general_settings_index'),
          ],
    },
    commissions: {
      active: 'Comissões',
      wide: true,
      width: 'w-[183px]',
      items: [
        navigationItem('Dashboard', 'inicio_index'),
        navigationItem('Imóveis', 'imoveis_index'),
        navigationItem('Leads', 'lais_leads_index'),
        navigationItem('Clientes', 'contacts_dashboard_index'),
        navigationItem('Atendimentos', 'home'),
        navigationItem('Propostas', 'lais_tenants_rent_flows', {
          activeOn: [
            'lais_tenants_rent_flows_status',
            'lais_tenants_rent_flows_status_phase',
          ],
        }),
        navigationItem('Vendas', 'operacao_imobiliaria_vendas', {
          activeOn: [
            'operacao_imobiliaria_vendas_detalhes',
            'operacao_imobiliaria_vendas_revisar',
            'operacao_imobiliaria_vendas_cadastro',
            'operacao_imobiliaria_vendas_nova',
          ],
        }),
        navigationItem('Financeiro', 'operacao_imobiliaria_financeiro', {
          activeOn: [
            'financeiro_index',
            'operacao_imobiliaria_financeiro_detalhes',
            'operacao_imobiliaria_financeiro_status',
            'operacao_imobiliaria_financeiro_status_alterar',
          ],
        }),
        navigationItem('Comissões', 'financeiro_comissoes_index', {
          activeOn: ['operacao_imobiliaria_vendas_comissao'],
        }),
        navigationItem('Relatórios', 'relatorio_index'),
        navigationItem('Configurações', 'general_settings_index'),
      ],
    },
  };

  return byModule[props.module] || byModule.sales;
});

const salesSettings = navigationItem('Configurações', 'general_settings_index');
const routeFor = item => {
  if (!item?.routeName || !router.hasRoute(item.routeName)) return null;
  if (route.params?.accountId === undefined || route.params.accountId === null) {
    return null;
  }

  return {
    name: item.routeName,
    params: { accountId: route.params.accountId, ...item.params },
    query: item.query,
  };
};
const isItemActive = item => {
  const names = [item?.routeName, ...(item?.activeOn || [])].filter(Boolean);
  if (names.includes(route.name)) return true;

  return !route.name && item?.label === configuration.value.active;
};
const isRoutable = item => Boolean(routeFor(item));
const canNavigate = item => isRoutable(item) && !isItemActive(item);
const navigateTo = item => {
  const destination = routeFor(item);
  if (!destination || isItemActive(item)) return;

  router.push(destination);
};

const iconFor = label => {
  const icon = {
    Vendas: 'i-lucide-tags',
    Propostas: 'i-lucide-file-text',
    Visitas: 'i-lucide-calendar-days',
    'Pós-venda': 'i-lucide-list-checks',
    Financeiro: 'i-lucide-circle-dollar-sign',
    Comissões: 'i-lucide-chart-no-axes-combined',
    Imóveis: 'i-lucide-building-2',
    Corretores: 'i-lucide-users-round',
    Clientes: 'i-lucide-users',
    Leads: 'i-lucide-contact-round',
    Mensagens: 'i-lucide-message-circle',
    Tarefas: 'i-lucide-list-todo',
    Configurações: 'i-lucide-settings',
    Relatórios: 'i-lucide-chart-column',
    Recebimentos: 'i-lucide-wallet-cards',
    Processos: 'i-lucide-git-branch',
    Documentos: 'i-lucide-files',
  };

  return icon[label] || 'i-lucide-layout-dashboard';
};
</script>

<template>
  <aside
    class="absolute inset-y-0 left-0 z-10 flex flex-col border-r border-[#eeeaf5] bg-white text-[#23366f]"
    :class="[
      configuration.width,
      configuration.compact
        ? 'py-4'
        : 'rounded-r-[20px] py-5 shadow-[5px_0_22px_rgba(45,26,89,0.04)]',
    ]"
  >
    <div
      class="flex items-center gap-2 px-8 text-[21px] font-black tracking-[-0.05em] text-[#171b4c]"
      :class="configuration.compact ? 'px-0 text-center text-[20px]' : 'justify-start px-8 text-[20px]'"
    >
      <template v-if="!configuration.compact">
        <span class="grid size-6 place-items-center rounded-md bg-gradient-to-br from-[#5420c5] to-[#8f2dda] text-white">
          <span class="i-lucide-panels-top-left size-4" />
        </span>
        <span>Imudei</span>
      </template>
      <span v-else-if="module === 'brokers'" class="mx-auto grid size-14 place-items-center rounded-xl bg-gradient-to-br from-[#5420c5] to-[#9c33dc] text-[22px] font-black text-white">im</span>
      <span v-else class="mx-auto text-[#5420c5]">Λ</span>
    </div>

    <nav
      v-if="!hideNavigation"
      class="grid px-2"
      :class="configuration.compact ? 'mt-6 gap-1 px-1' : 'mt-10 gap-2'"
      :aria-label="configuration.active"
    >
      <button
        v-for="item in configuration.items"
        :key="item.label"
        type="button"
        class="flex items-center gap-1 rounded-lg px-1 text-left font-semibold transition-colors"
        :class="[
          isItemActive(item)
            ? 'bg-[#f0eafe] text-[#5b20c5]'
            : isRoutable(item)
              ? 'text-[#7987a9]'
              : 'cursor-not-allowed text-[#aeb6c8] opacity-70',
          configuration.compact
            ? 'h-9 justify-center px-0 text-[8px]'
            : configuration.wide
              ? 'h-12 justify-start gap-3 px-3 text-[11px]'
              : 'justify-center text-[8px]',
        ]"
        :aria-disabled="!isRoutable(item)"
        :disabled="!canNavigate(item)"
        :title="isRoutable(item) ? item.label : `${item.label}: rota indisponível`"
        @click="navigateTo(item)"
      >
        <span :class="iconFor(item.label)" class="size-3 shrink-0" />
        <span v-if="!configuration.compact" class="truncate">{{ item.label }}</span>
      </button>
    </nav>

    <div v-if="module === 'sales'" class="mt-auto px-5 text-[9px] font-medium text-[#53618c]">
      <button class="flex h-10 w-full cursor-not-allowed items-center gap-3 border-b border-[#f0edf5] opacity-70" type="button" disabled aria-disabled="true" title="Ajuda: rota indisponível"><span class="i-lucide-circle-help size-4" />Ajuda</button>
      <button class="flex h-10 w-full items-center gap-3 border-b border-[#f0edf5] disabled:cursor-not-allowed disabled:opacity-70" type="button" :disabled="!canNavigate(salesSettings)" :aria-disabled="!isRoutable(salesSettings)" @click="navigateTo(salesSettings)"><span class="i-lucide-settings size-4" />Configurações</button>
      <button class="mt-4 flex h-9 w-full cursor-not-allowed items-center gap-3 opacity-70" type="button" disabled aria-disabled="true" title="Recolher menu: ação indisponível"><span class="i-lucide-chevron-left size-4" />Recolher menu</button>
    </div>
    <div v-else-if="!hideNavigation" class="mt-auto flex items-center gap-2 px-4" :class="configuration.compact ? 'justify-center px-0' : ''">
      <span class="grid size-8 place-items-center rounded-full bg-[#f0eafe] text-[9px] font-bold text-[#5420be]">{{ currentUserInitials }}</span>
      <span v-if="!configuration.compact" class="min-w-0">
        <b class="block truncate text-[10px] text-[#172968]">{{ currentUserName }}</b>
        <small class="block text-[8px] text-[#8a94b0]">{{ currentUserRole }}</small>
      </span>
    </div>
  </aside>
</template>
